import React, { useEffect, useState } from "react";
import { ArrowRight, Disc3, Star, Tv, Flame, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { readStreak, CHART_KEY } from "../lib/streak";
import { ChartLeaderboard } from "./ChartLeaderboard";
import { TV, TvKicker } from "./TriviaTheme";

const DECADES = [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020];
export const CHART_NAMES = {
  "uk-singles": "UK Singles Chart", "uk-albums": "UK Albums Chart",
  "us-singles": "Billboard Hot 100", "us-albums": "Billboard 200",
};
const SINCE = { "uk-singles": "November 1952", "uk-albums": "July 1956", "us-singles": "August 1958", "us-albums": "January 1950" };

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex p-1 rounded-full bg-[#EEE8DD] border border-[#DDD5C7]" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
        className={`px-4 py-1.5 rounded-full font-ui text-xs sm:text-[13px] font-semibold transition-[background-color,color] ${value === v ? "bg-[#161514] text-white" : "text-[#4A453E] hover:text-[#161514]"}`}>
        {l}
      </button>
    ))}
  </div>
);

export const ChartRounds = ({ onPlay, onChartNight }) => {
  const [region, setRegion] = useState(() => localStorage.getItem("cv_chart_region") || "uk");
  const [chart, setChart] = useState("singles");
  const [packs, setPacks] = useState(null);
  const key = `${region}-${chart}`;
  useEffect(() => { localStorage.setItem("cv_chart_region", region); }, [region]);
  useEffect(() => {
    setPacks(null);
    api.get("/trivia/packs", { params: { region, chart } }).then((r) => setPacks(r.data)).catch(() => setPacks({}));
  }, [region, chart]);
  const play = (extra) => onPlay({ region, chart, ...extra });
  const noun = chart === "singles" ? "single" : "album";
  return (
    <div className="mt-10" data-testid="special-rounds">
      <TvKicker line={false}>Special rounds</TvKicker>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mt-2 mb-4">
        <h2 className="font-tv-serif font-bold text-xl sm:text-2xl text-[#161514]" data-testid="chart-heading">
          {region === "uk" ? "British" : "American"} chart music
        </h2>
        <div className="flex flex-wrap gap-2">
          <Seg testid="region-toggle" value={region} onChange={setRegion} options={[["uk", "UK"], ["us", "US"]]} />
          <Seg testid="chart-toggle" value={chart} onChange={setChart} options={[["singles", "Singles"], ["albums", "Albums"]]} />
        </div>
      </div>
      <ChartThreeCard region={region} />
      <div className="mt-3"><ChartLeaderboard region={region} /></div>
      {packs && (
        <div className="grid sm:grid-cols-5 gap-3 mt-3" key={key}>
          <NumberOnesCard pack={packs["number-ones"]} chartKey={key} noun={noun} onPlay={play} />
          <NameTheYearCard count={packs["name-the-year"]?.total || 0} noun={noun} onPlay={play} />
          <ArtistCard region={region} chart={chart} onPlay={play} />
          <ChristmasCard pack={packs["christmas-number-ones"]} chartKey={key} noun={noun} onPlay={play} />
          <ChartNightCard chartKey={key} onOpen={() => onChartNight({ region, chart })} />
        </div>
      )}
    </div>
  );
};

const NumberOnesCard = ({ pack, chartKey, noun, onPlay }) => {
  const [decade, setDecade] = useState(0);
  if (!pack) return null;
  const n = decade ? pack.decades?.[String(decade)] || 0 : pack.total;
  return (
    <div className={`${TV.card} sm:col-span-3 p-5 flex flex-col tv-rise`} data-testid="number-ones-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="tv-caps text-[#57524B]">{CHART_NAMES[chartKey]} No.1s</p>
          <p className="font-ui font-bold text-lg text-[#161514] mt-2 leading-snug">Every number-one {noun} since {SINCE[chartKey]}</p>
        </div>
        <span className="w-9 h-9 shrink-0 rounded-full bg-[#EEE8DD] flex items-center justify-center"><Disc3 className="w-4 h-4 text-[#161514]" /></span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4" data-testid="decade-picker">
        {[0, ...DECADES].filter((d) => !d || pack.decades?.[String(d)]).map((d) => (
          <button key={d} data-testid={`decade-pill-${d || "all"}`} onClick={() => setDecade(d)}
            className={`px-2.5 py-1 rounded-full font-ui text-xs font-semibold border transition-[background-color,color] ${decade === d ? "bg-[#161514] text-white border-[#161514]" : "bg-[#EEE8DD] text-[#4A453E] border-[#DDD5C7] hover:bg-[#E6DFD2]"}`}>
            {d ? `${String(d).slice(2)}s` : "All"}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 mt-auto pt-5">
        <span className="font-ui text-xs text-[#77716A]" data-testid="number-ones-count">{n.toLocaleString()} questions{decade ? ` from the ${decade}s` : ""}</span>
        <button data-testid="play-number-ones" disabled={!n} onClick={() => onPlay({ pack: "number-ones", ...(decade ? { decade: String(decade) } : {}) })}
          className={`${TV.pillDark} inline-flex items-center gap-1.5 px-4 py-2 font-ui text-sm font-semibold disabled:opacity-40`}>
          Play round <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const NameTheYearCard = ({ count, noun, onPlay }) => !count ? null : (
  <div className={`${TV.card} sm:col-span-2 p-5 flex flex-col tv-rise`} data-testid="name-the-year-card">
    <p className="tv-caps text-[#57524B]">Name the Year</p>
    <p className="font-ui font-bold text-lg text-[#161514] mt-2 leading-snug">Three No.1s. One year.</p>
    <p className="font-ui text-[13px] text-[#77716A] mt-1">Spot the year from three chart-topping {noun}s. {count} sets, with a shareable snapshot card.</p>
    <button data-testid="play-name-the-year" onClick={() => onPlay({ pack: "name-the-year" })}
      className={`${TV.pillDark} mt-5 self-start inline-flex items-center gap-1.5 px-4 py-2 font-ui text-sm font-semibold`}>
      Play round <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

const ArtistCard = ({ region, chart, onPlay }) => {
  const [artists, setArtists] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => {
    api.get("/trivia/artists", { params: { region, chart } })
      .then((r) => { setArtists(r.data); setPick(r.data[0]?.artist || ""); }).catch(() => setArtists([]));
  }, [region, chart]);
  if (!artists.length) return null;
  const cur = artists.find((a) => a.artist === pick);
  return (
    <div className={`${TV.card} sm:col-span-3 p-5 flex flex-col tv-rise`} data-testid="artist-round-card">
      <p className="tv-caps text-[#57524B]">Artist rounds · {artists.length} artists</p>
      <p className="font-ui font-bold text-lg text-[#161514] mt-2 leading-snug">Pick a favourite, quick-fire their No.1s</p>
      <select data-testid="artist-select" value={pick} onChange={(e) => setPick(e.target.value)}
        className="mt-4 w-full rounded-full border border-[#DDD5C7] bg-[#F7F4EE] px-4 py-2.5 font-ui text-sm text-[#161514] focus:outline-none focus:border-[#2D4A3E]">
        {artists.map((a) => <option key={a.artist} value={a.artist}>{`${a.artist} (${a.count} questions)`}</option>)}
      </select>
      <div className="flex items-center justify-between gap-3 mt-auto pt-4">
        <span className="font-ui text-xs text-[#77716A]" data-testid="artist-count">{cur ? `${cur.count} questions` : ""}</span>
        <button data-testid="play-artist-round" disabled={!pick} onClick={() => onPlay({ pack: "artist-rounds", artist: pick })}
          className={`${TV.pillDark} inline-flex items-center gap-1.5 px-4 py-2 font-ui text-sm font-semibold disabled:opacity-40 max-w-[60%]`}>
          <span className="truncate">Play {pick.split(" ").slice(0, 2).join(" ")}</span> <ArrowRight className="w-4 h-4 shrink-0" />
        </button>
      </div>
    </div>
  );
};

const ChristmasCard = ({ pack, chartKey, noun, onPlay }) => !pack ? null : (
  <div className={`${TV.green} sm:col-span-2 p-5 flex flex-col relative overflow-hidden tv-rise`} data-testid="christmas-card">
    <Star className="absolute -right-3 -top-3 w-24 h-24 text-[#C5A059]/25" fill="currentColor" />
    <p className="tv-caps text-white/70 relative">Festive round</p>
    <p className="font-ui font-bold text-lg mt-2 leading-snug relative">Christmas No.1s</p>
    <p className="font-ui text-[13px] text-white/75 mt-1 relative">Every {CHART_NAMES[chartKey]} Christmas number-one {noun}: {pack.total} festive chart-toppers.</p>
    <button data-testid="play-christmas" onClick={() => onPlay({ pack: "christmas-number-ones" })}
      className="self-start inline-flex items-center gap-1.5 bg-white text-[#161514] hover:bg-[#F4F1EA] rounded-full px-4 py-2 font-ui text-sm font-semibold transition-colors relative mt-5">
      Play festive round <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

const ChartNightCard = ({ chartKey, onOpen }) => (
  <button data-testid="chart-night-card" onClick={onOpen}
    className="sm:col-span-5 text-left bg-[#161514] text-white rounded-[20px] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:-translate-y-0.5 transition-transform tv-rise">
    <div className="flex items-start gap-4">
      <span className="w-11 h-11 shrink-0 rounded-full bg-white/10 flex items-center justify-center"><Tv className="w-5 h-5" /></span>
      <div>
        <p className="tv-caps text-white/60">Host mode · Club</p>
        <p className="font-ui font-bold text-lg sm:text-xl mt-1">Group Chart Night</p>
        <p className="font-ui text-[13px] text-white/70 mt-1">A big-screen, decade-by-decade countdown of {CHART_NAMES[chartKey]} No.1s, with team scores.</p>
      </div>
    </div>
    <span className="shrink-0 self-start sm:self-center inline-flex items-center gap-1.5 bg-white text-[#161514] rounded-full px-4 py-2 font-ui text-sm font-semibold">Host a night <ArrowRight className="w-4 h-4" /></span>
  </button>
);

const ChartThreeCard = ({ region }) => {
  const nav = useNavigate();
  const [streak] = useState(() => readStreak(CHART_KEY));
  const tag = region.toUpperCase();
  return (
    <div className={`${TV.green} p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 tv-rise`} data-testid="chart-three-card">
      <div>
        <p className="tv-caps text-white/70">Free daily · 3 questions</p>
        <p className="font-ui font-bold text-2xl sm:text-3xl mt-1" data-testid="chart-three-title">{tag} Chart Three</p>
        <p className="font-ui text-[13px] text-white/75 mt-1">A singles No.1, an albums No.1 and a Name the Year. New every day.</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span data-testid="chart-streak" className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 font-ui text-xs font-semibold">
          {streak.playedToday ? <Check className="w-3.5 h-3.5" /> : <Flame className="w-3.5 h-3.5" />}
          {streak.count ? `${streak.count}-day chart streak` : "Start a chart streak"}
        </span>
        <button data-testid="play-chart-three" onClick={() => nav(`/trivia/play?source=chart-daily&region=${region}&mode=mc&count=3`)}
          className="inline-flex items-center gap-1.5 bg-white text-[#161514] hover:bg-[#F4F1EA] rounded-full px-5 py-2.5 font-ui text-sm font-semibold transition-colors">
          {streak.playedToday ? "Play again" : "Play today's"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
